import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocalSearchParams, usePathname, useRouter } from "expo-router";
// @ts-expect-error baseline 2026-02-16
import { WebLayout } from "@src/components/web";
import { View } from "@src/components";
import { DataStateView } from "@src/components/screen-state";
import { useCameras } from "@src/features/cameras";
import { useSiteNames } from "@src/features/cameras/hooks/useSiteNames";
import { useTenantId } from "@src/features/tenants";
import { useTrafficFlowScenarios, buildConfigByUuid } from "../hooks";
import { groupScenariosBySite, type ScenarioSiteGroup } from "../utils/scenario-group";
import { ScenarioGrid } from "../components/ScenarioGrid.web";
import { ScenarioDetailPanel } from "../components/ScenarioDetailPanel.web";
import { ScenarioMap } from "../components/ScenarioMap.web";
import { MapCanvas } from "../components/MapCanvas.web";
import { ViewModeToggle, type TrafficFlowViewMode } from "../components/ViewModeToggle.web";

const VIEW_MODE_KEY = "traffic-flow:view-mode";

function loadViewMode(): TrafficFlowViewMode {
  if (typeof window === "undefined") return "grid";
  const v = window.localStorage.getItem(VIEW_MODE_KEY);
  return v === "map" ? "map" : "grid";
}

interface TrafficFlowScreenProps {
  siteIdFromRoute?: string;
}

export default function TrafficFlowScreen({ siteIdFromRoute }: TrafficFlowScreenProps = {}) {
  const {
    data: cameras,
    isLoading: isCamerasLoading,
    isFetching: isCamerasFetching,
    error: camerasError,
    refetch: refetchCameras,
  } = useCameras();
  const {
    data: scenarios,
    isLoading: isScenariosLoading,
    isFetching: isScenariosFetching,
    error: scenariosError,
    refetch: refetchScenarios,
    isTenantPending,
  } = useTrafficFlowScenarios();
  const selectedSiteId = siteIdFromRoute;
  const tenantId = useTenantId();
  const siteNames = useSiteNames();
  const configByUuid = useMemo(() => buildConfigByUuid(scenarios), [scenarios]);

  const allGroups = useMemo(
    () => (cameras && scenarios ? groupScenariosBySite(scenarios, cameras, configByUuid, siteNames) : undefined),
    [cameras, scenarios, configByUuid, siteNames],
  );

  const filteredGroups = useMemo(() => {
    if (!allGroups) return undefined;
    if (!selectedSiteId) return allGroups;
    return allGroups.filter((g) => g.siteId === selectedSiteId);
  }, [allGroups, selectedSiteId]);

  const selectedSiteName = useMemo(() => {
    if (!selectedSiteId || !allGroups) return null;
    return allGroups.find((g) => g.siteId === selectedSiteId)?.siteName ?? null;
  }, [allGroups, selectedSiteId]);

  const { selectedScenario } = useLocalSearchParams<{ selectedScenario?: string }>();
  const detailRow = useMemo(() => {
    if (!selectedScenario || !allGroups) return null;
    for (const group of allGroups) {
      const row = group.rows.find((r) => r.scenarioUuid === selectedScenario);
      if (row) return row;
    }
    return null;
  }, [allGroups, selectedScenario]);

  const rightPanel =
    detailRow ?
      <ScenarioDetailPanel
        key={detailRow.scenarioUuid}
        siteId={detailRow.siteId}
        assetCameraKey={detailRow.assetCameraKey}
        scenarioUuid={detailRow.scenarioUuid}
        scenarioName={detailRow.scenarioName}
        cameraDisplayName={detailRow.cameraDisplayName}
      />
    : undefined;

  const [viewMode, setViewMode] = useState<TrafficFlowViewMode>(loadViewMode);
  useEffect(() => {
    if (typeof window !== "undefined") window.localStorage.setItem(VIEW_MODE_KEY, viewMode);
  }, [viewMode]);

  const flatRows = useMemo(() => (filteredGroups ?? []).flatMap((g) => g.rows), [filteredGroups]);
  const router = useRouter();
  const pathname = usePathname();
  const handleScenarioSelect = useCallback(
    (scenarioUuid: string) => {
      if (selectedScenario === scenarioUuid) {
        router.replace(pathname as never);
        return;
      }
      const row = flatRows.find((r) => r.scenarioUuid === scenarioUuid);
      const params = new URLSearchParams({ selectedScenario: scenarioUuid });
      if (row?.scenarioName) params.set("scenarioName", row.scenarioName);
      router.replace(`${pathname}?${params.toString()}` as never);
    },
    [flatRows, pathname, router, selectedScenario],
  );

  return (
    <WebLayout rightPanel={rightPanel}>
      <View flex={1} style={{ paddingTop: 16, paddingHorizontal: 24, gap: 16 }}>
        <ViewModeToggle value={viewMode} onChange={setViewMode} />
        <DataStateView<ScenarioSiteGroup[]>
          data={filteredGroups}
          isLoading={isTenantPending || isCamerasLoading || isScenariosLoading}
          isFetching={isCamerasFetching || isScenariosFetching}
          error={camerasError ?? scenariosError ?? null}
          refetch={() => {
            void refetchCameras();
            void refetchScenarios();
          }}
          isEmpty={(g) => g.reduce((sum, group) => sum + group.rows.length, 0) === 0}
          loadingMessage="Loading scenarios..."
          errorTitle="Unable to load scenarios"
          emptyState={
            selectedSiteId ?
              {
                icon: "motion",
                title: `No scenarios at ${selectedSiteName ?? "this site"}`,
                message: "Pick a different site, or 'All sites' to see crossline mapping scenarios from elsewhere.",
              }
            : {
                icon: "motion",
                title: "No crossline mapping scenarios reported yet",
                message: "Cameras configured for crossline mapping will appear here once they publish scenario status.",
              }
          }
        >
          {(groups) =>
            viewMode === "map" ?
              <ScenarioMap
                rows={flatRows}
                configByUuid={configByUuid}
                tenantId={tenantId}
                selectedScenarioUuid={selectedScenario ?? null}
                onScenarioSelect={handleScenarioSelect}
              />
            : <View flex={1} style={{ gap: 16, minHeight: 0 }}>
                <MapCanvas
                  rows={flatRows}
                  configByUuid={configByUuid}
                  tenantId={tenantId}
                  selectedScenarioUuid={selectedScenario ?? null}
                  onScenarioSelect={handleScenarioSelect}
                  height={260}
                />
                <View flex={1} style={{ minHeight: 0 }}>
                  <ScenarioGrid groups={groups} selectedSiteName={selectedSiteName} />
                </View>
              </View>
          }
        </DataStateView>
      </View>
    </WebLayout>
  );
}
